import type { ReactNode } from 'react';
import { useNavigate, getRouteApi } from '@tanstack/react-router';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { useTranslation } from '@hooks/useTranslation';
import { STATIC_BASE } from '@lib/runtimeConfig';
import CheckIcon from '@assets/icons/CheckIcon';
import styles from './WorkspaceNotFound.module.scss';

const routeApi = getRouteApi('/workspace-not-found');
const SUPPORT_EMAIL = 'eor_support@myworkpay.com';

/**
 * Full-page "Organization not found" dead-end (WP-6669). The api-gateway
 * 302s here from /login when a subdomain has no Keycloak realm
 * (?workspace= carries the failed slug). Pre-auth; mirrors the design
 * prototype's NotFoundPage 1:1.
 */
export function WorkspaceNotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspace } = routeApi.useSearch();

  const tips = [
    t('K_DS_EXT_WNF_TIP_1', 'Check the workspace address for typos'),
    t('K_DS_EXT_WNF_TIP_2', 'Confirm your company still uses this Workpay subdomain'),
    t('K_DS_EXT_WNF_TIP_3', 'Ask your HR admin for the correct workspace link'),
  ];

  let subtitle: ReactNode;
  if (workspace) {
    const template = t(
      'K_DS_EXT_WNF_SUB',
      "We couldn't find a Workpay workspace at {workspace}. It may have been renamed or removed, or the address may be mistyped.",
    );
    const parts = template.split('{workspace}');
    subtitle = (
      <>
        {parts[0] ?? ''}
        <strong className={styles.slug}>&quot;{workspace}&quot;</strong>
        {parts[1] ?? ''}
      </>
    );
  } else {
    subtitle = t(
      'K_DS_EXT_WNF_SUB_GENERIC',
      "We couldn't find the Workpay workspace you're looking for. It may have been renamed or removed, or the address may be mistyped.",
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <img
          src={`${STATIC_BASE}/images/eor-login/workpay-logo.svg`}
          alt="Workpay"
          className={styles.logo}
        />
      </header>

      <main className={styles.main}>
        <div className={styles.content}>
          <img
            src={`${STATIC_BASE}/images/not-found.png`}
            alt=""
            className={styles.illustration}
            draggable={false}
          />

          <h1 className={styles.title}>{t('K_DS_EXT_WNF_TITLE', 'Organization not found')}</h1>
          <p className={styles.subtitle}>{subtitle}</p>

          <div className={styles.tips}>
            <p className={styles.tipsTitle}>{t('K_DS_EXT_WNF_TIPS_TITLE', 'Things to try')}</p>
            <ul className={styles.tipList}>
              {tips.map((tip) => (
                <li key={tip}>
                  <CheckIcon className={styles.check} />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.actions}>
            <Button
              variant="primary"
              size="xxl"
              className={styles.findButton}
              onClick={() => void navigate({ to: '/eor/find' })}
            >
              {t('K_DS_EXT_WNF_FIND_WS', 'Find your workspace')}
            </Button>
            <a className={styles.supportLink} href={`mailto:${SUPPORT_EMAIL}`}>
              {t('K_DS_EXT_WNF_CONTACT_SUPPORT', 'Contact Workpay support')}
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
